import { useState } from 'react'
import { DateTime } from 'luxon'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import MaskedInputService from 'services/maskedInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import bankingAccountValidator from 'validators/bankingAccountValidator'
import { isValid as isValidCpf } from '@fnando/cpf'
import { FaArrowRight, FaUniversity, FaExclamationCircle } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Container,
  Content,
  Card,
  CardHeader,
  CardTitle,
  Caption,
  Row,
  Form,
  FormField,
  FieldLabel,
  FieldHeader,
  FormActions,
  LinkButton,
  EmptyState,
  Divider,
  WarningText,
  MaskedInput,
  CheckRow,
  CheckLabel,
  MissingFieldGrid,
  MissingFieldChip
} from '../../styles'

export default function OpenAccountForm ({
  readiness,
  requestConfig,
  onAccountOpened: handleAccountOpened = () => {}
}) {
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [requestError, setRequestError] = useState (null)
  const isBusiness = BankingService.isBusinessOwner ()
  const canOpenAccount = BankingService.canOpenAccount ()
  const missingFields = readiness?.missingFields ?? []

  // O documento do cadastro, sem máscara, apenas como sugestão do campo.
  const registeredDocument = (readiness?.cpfCnpj ?? '').replace (/\D/g, '')

  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  if (!canOpenAccount) return (
    <Container>
      <Content>
        <Card>
          <EmptyState>
            <FaUniversity size={26}/>
            <b>Nenhuma conta encontrada</b>
            <p>Apenas salões e profissionais podem abrir uma conta Cutilei</p>
          </EmptyState>
        </Card>
      </Content>
    </Container>
  )

  /*
    Cadastro incompleto é a maior causa de recusa no onboarding, então o titular é
    mandado para as configurações antes de tentar abrir a conta.
  */
  if (missingFields.length > 0) return (
    <Container>
      <Content>
        <Card gap={12}>
          <CardHeader>
            <CardTitle>Complete seu cadastro</CardTitle>
            <FaExclamationCircle size={16} color='#FF9800'/>
          </CardHeader>
          <Caption>
            {readiness?.message ?? 'Complete seu cadastro para abrir sua conta bancária'}
          </Caption>
          <Divider/>
          <Caption>Faltam estas informações:</Caption>
          <MissingFieldGrid>
            {missingFields.map ((field, index) => (
              <MissingFieldChip key={index}>
                {BankingService.getMissingFieldLabel (field)}
              </MissingFieldChip>
            ))}
          </MissingFieldGrid>
          <FormActions align='flex-start'>
            <LinkButton to='/business/config' state={{configOption: 'personal_data'}}>
              Completar cadastro
              <FaArrowRight size={12} color='#252525'/>
            </LinkButton>
          </FormActions>
        </Card>
      </Content>
    </Container>
  )

  return (
    <Container>
      <Content>
        <Formik
          validationSchema={bankingAccountValidator}
          initialValues={{
            /*
              O documento do cadastro entra preenchido, e o titular troca na hora se
              precisar — é ele que abre a conta no Asaas, não o que está cadastrado.

              `use_cnpj` sai do próprio documento e não do tipo de titular: salão
              cadastrado com CPF existe, e no Asaas ele é pessoa física.
            */
            is_business: isBusiness,
            use_cnpj: isBusiness && !isValidCpf (registeredDocument),
            cpfCnpj: registeredDocument,
            incomeValue: 0,
            companyType: null,
            birthDate: ''
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              setSubmitting (true)
              setRequestError (null)

              const { data: account } = await cutileiApi.post ('/banking_account', {
                cpfCnpj: values.cpfCnpj,
                incomeValue: values.incomeValue,
                ...values.use_cnpj && {companyType: values.companyType?.value},

                // Só o salão informa a data: a do profissional já está no cadastro, e a
                // API a lê de lá quando o campo não vem.
                ...(isBusiness && !values.use_cnpj) && {
                  birthDate: DateTime.fromFormat (values.birthDate, 'ddMMyyyy').toISODate ()
                }
              }, requestConfig)

              setSubmitting (false)
              handleAccountOpened (account)
              toggleOkDialog ()
            } catch (error) {
              setSubmitting (false)
              console.log (error.response ?? error)
              setRequestError (
                error.response?.data?.message ?? 'Não foi possível abrir sua conta'
              )
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleBlur,
            handleSubmit
          }) => {
            /*
              Trocar o tipo limpa o campo: um CNPJ digitado não vira CPF ao mudar a
              máscara, ele só passaria a ser lido errado.
            */
            const handleDocumentTypeChange = useCnpj => {
              if (values.use_cnpj === useCnpj) return

              setFieldValue ('cpfCnpj', '')
              setFieldValue ('use_cnpj', useCnpj)
            }

            return (
              <Form>
                <CardHeader>
                  <CardTitle>Abrir minha conta Cutilei</CardTitle>
                  <FaUniversity size={16} color='#FF9800'/>
                </CardHeader>
                <Caption>
                  {isBusiness
                    ? 'A conta Cutilei é a conta digital do seu salão: receba por Pix, '
                      + 'pague boletos, repasse comissões para a sua equipe e saque o '
                      + 'saldo para a sua conta bancária'
                    : 'A conta Cutilei é a sua conta digital: receba as suas comissões e '
                      + 'transferências por Pix, pague boletos e saque o saldo para a sua '
                      + 'conta bancária'}
                </Caption>
                <Caption>
                  {'O endereço e o restante do seu cadastro na Cutilei serão usados para '
                  + 'abrir a conta. Confira o documento e informe os dados abaixo'}
                </Caption>
                <Divider/>
                {/*
                  Os cabeçalhos reservam a mesma altura nos três campos, então as entradas
                  nascem alinhadas e um aviso que quebre em duas linhas cresce para baixo,
                  sem mexer nos vizinhos.
                */}
                <Row>
                  <FormField stretch={3} maxWidth={220}>
                    <FieldHeader withOptions={isBusiness}>
                      <FieldLabel>{isBusiness ? 'CPF ou CNPJ' : 'CPF'}</FieldLabel>
                      {isBusiness && (
                        <Row style={{gap: 15, justifyContent: 'center'}}>
                          <CheckRow>
                            <CheckBox
                              id='use_cnpj'
                              value={values.use_cnpj}
                              onValueChange={() => handleDocumentTypeChange (true)}
                            />
                            <CheckLabel onClick={() => handleDocumentTypeChange (true)}>
                              Usar CNPJ
                            </CheckLabel>
                          </CheckRow>
                          <CheckRow>
                            <CheckBox
                              id='use_cpf'
                              value={!values.use_cnpj}
                              onValueChange={() => handleDocumentTypeChange (false)}
                            />
                            <CheckLabel onClick={() => handleDocumentTypeChange (false)}>
                              Usar CPF
                            </CheckLabel>
                          </CheckRow>
                        </Row>
                      )}
                    </FieldHeader>
                    {/* A máscara muda com o tipo, e o Cleave só a troca ao remontar. */}
                    <MaskedInput
                      key={values.use_cnpj ? 'cnpj' : 'cpf'}
                      name='cpfCnpj'
                      placeholder={values.use_cnpj ? 'CNPJ' : 'CPF'}
                      options={{
                        blocks: values.use_cnpj ? [2, 3, 3, 4, 2] : [3, 3, 3, 2],
                        delimiters: values.use_cnpj ? ['.', '.', '/', '-'] : ['.', '.', '-'],
                        numericOnly: true
                      }}
                      value={values.cpfCnpj}
                      onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                      onBlur={handleBlur ('cpfCnpj')}
                    />
                    {touched.cpfCnpj && errors.cpfCnpj && (
                      <WarningText>{errors.cpfCnpj}</WarningText>
                    )}
                  </FormField>
                  <FormField stretch={3} maxWidth={220}>
                    <FieldHeader withOptions={isBusiness}>
                      <FieldLabel>
                        {isBusiness ? 'Faturamento mensal' : 'Renda mensal'}
                      </FieldLabel>
                    </FieldHeader>
                    <MoneyInput
                      name='incomeValue'
                      placeholder='R$ 0,00'
                      value={values.incomeValue}
                      onValueChange={setFieldValue}
                      onBlur={handleBlur ('incomeValue')}
                    />
                    {touched.incomeValue && errors.incomeValue && (
                      <WarningText>{errors.incomeValue}</WarningText>
                    )}
                  </FormField>
                  {/*
                    Some junto com o CNPJ: no Asaas o tipo de empresa não existe para pessoa
                    física, e mandá-lo em uma conta de CPF faz a criação ser recusada.
                  */}
                  {values.use_cnpj && (
                    <FormField stretch={3} maxWidth={220}>
                      <FieldHeader withOptions={isBusiness}>
                        <FieldLabel>Tipo de empresa</FieldLabel>
                      </FieldHeader>
                      <Select
                        name='companyType'
                        placeholder='Selecione'
                        options={BankingService.COMPANY_TYPES}
                        value={values.companyType}
                        error={errors.companyType}
                        touched={touched.companyType}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                      />
                      {touched.companyType && errors.companyType && (
                        <WarningText>{errors.companyType}</WarningText>
                      )}
                    </FormField>
                  )}
                  {/*
                    Ocupa a mesma vaga do tipo de empresa, do outro lado da moeda: o Asaas
                    exige a data de nascimento em toda conta de pessoa física, e o cadastro
                    do salão não a tem. O profissional não vê o campo porque a data dele já
                    está cadastrada.
                  */}
                  {isBusiness && !values.use_cnpj && (
                    <FormField stretch={3} maxWidth={220}>
                      <FieldHeader withOptions={isBusiness}>
                        <FieldLabel>Nascimento do titular</FieldLabel>
                      </FieldHeader>
                      <MaskedInput
                        name='birthDate'
                        placeholder='dd/mm/aaaa'
                        options={{date: true, datePattern: ['d', 'm', 'Y']}}
                        value={values.birthDate}
                        onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                        onBlur={handleBlur ('birthDate')}
                      />
                      {touched.birthDate && errors.birthDate && (
                        <WarningText>{errors.birthDate}</WarningText>
                      )}
                    </FormField>
                  )}
                </Row>
                {requestError && <WarningText>{requestError}</WarningText>}
                <AlertDialog
                  visible={showOkDialog}
                  title='Sucesso'
                  message='Conta criada! Agora envie os documentos para concluir a abertura'
                  confirmText='Ok'
                  onClose={toggleOkDialog}
                />
                <FormActions>
                  <MainButton
                    label='Abrir conta'
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    width={160}
                  />
                </FormActions>
              </Form>
            )
          }}
        </Formik>
      </Content>
    </Container>
  )
}
